import { useCallback, useEffect, useMemo, useRef } from 'react'

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { handleNotAwaitedPromise } from '@valian/react-kitchen-sink'
import dayjs from 'dayjs'
import { useLocalStorage } from 'usehooks-ts'

import { useAuthenticatedFirebaseUser } from '@/auth/useFirebaseUser'
import { useNotificationSound } from '@/hooks/useNotificationSound'
import { useNow } from '@/hooks/useNow'

import { countsTowardAgentBadge } from './agentQueue'
import { agentOpenSupportRequestsQuery, agentSettledSupportRequestsQuery } from './supportRequests'
import { type SupportRequestAgentDocument } from './types'
import { retentionStart, useLoadState } from './useRequesterSupportRequests'
import { useSupportReads } from './useSupportReads'

const AGENT_SOUND_STORAGE_KEY = 'soutien.agentSoundEnabled'

export const useAgentSupportRequests = () => {
  const queryClient = useQueryClient()
  const now = useNow()
  const settledSince = useMemo(() => dayjs(retentionStart(new Date())).startOf('hour').toDate(), [])

  const open = useQuery(agentOpenSupportRequestsQuery())
  const settled = useQuery(agentSettledSupportRequestsQuery(settledSince))

  const requests = useMemo(() => {
    const cutoff = retentionStart(now)
    const byNumber = new Map<number, SupportRequestAgentDocument>()
    for (const request of [...(open.data?.data ?? []), ...(settled.data?.data ?? [])]) {
      if (request.status !== 'settled' || (request.settledAt && request.settledAt >= cutoff)) {
        byNumber.set(request.number, request)
      }
    }
    return [...byNumber.values()]
  }, [open.data, settled.data, now])

  const { state: loadState, restart } = useLoadState([open, settled])

  const retry = useCallback(() => {
    restart()
    handleNotAwaitedPromise(queryClient.resetQueries({ queryKey: ['supportRequests', 'agent'] }))
  }, [queryClient, restart])

  return { requests, loadState, retry }
}

export const useSupportAgentUnreadCount = (requests: ReadonlyArray<SupportRequestAgentDocument>) => {
  const { supportRole } = useAuthenticatedFirebaseUser()
  const { isUnreadByAgent } = useSupportReads()
  return supportRole === null
    ? 0
    : requests.filter((request) => countsTowardAgentBadge(supportRole, request.status) && isUnreadByAgent(request))
        .length
}

export const useSupportAgentSoundPreference = () => useLocalStorage(AGENT_SOUND_STORAGE_KEY, true)

const latestAlertAt = (requests: ReadonlyArray<SupportRequestAgentDocument>, uid: string, line: 'l1' | 'l2') =>
  Math.max(
    0,
    ...requests.map(({ lastAlert }) =>
      lastAlert && lastAlert.by !== uid && lastAlert.audience.includes(line) ? lastAlert.at.getTime() : 0
    )
  )

export const useSupportAgentAlerts = (
  requests: ReadonlyArray<SupportRequestAgentDocument>,
  { isLoaded }: { isLoaded: boolean }
) => {
  const { firebaseUser, supportRole } = useAuthenticatedFirebaseUser()
  const [soundEnabled] = useSupportAgentSoundPreference()
  const { playNotificationSound } = useNotificationSound()
  const heardUpTo = useRef<number | null>(null)

  useEffect(() => {
    if (!isLoaded || !supportRole) {
      return
    }
    const latest = latestAlertAt(requests, firebaseUser.uid, supportRole)
    if (heardUpTo.current === null) {
      heardUpTo.current = latest
      return
    }
    if (latest <= heardUpTo.current) {
      return
    }
    heardUpTo.current = latest
    if (soundEnabled) {
      playNotificationSound('support', `${supportRole}:${firebaseUser.uid}:${latest}`)
    }
  }, [requests, isLoaded, supportRole, firebaseUser.uid, soundEnabled, playNotificationSound])
}
